import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, ClipboardCheck, Clock, AlertCircle, CheckCircle2, Trash2, User as UserIcon, Calendar, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";
import PolicyRemindersBanner from "@/components/PolicyRemindersBanner";

const PRIORITY_META = {
  high: { label: "Hoch", color: "bg-prime text-white", dot: "bg-prime" },
  medium: { label: "Mittel", color: "bg-amber-500 text-white", dot: "bg-amber-500" },
  low: { label: "Niedrig", color: "bg-slate-400 text-white", dot: "bg-slate-400" },
};

export default function TasksPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [filter, setFilter] = useState("mine_open"); // mine_open | mine_all | all_open | all
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({
    title: "", description: "", due_date: "", priority: "medium",
    assigned_to: user?.id || "", client_id: "",
  });

  const load = async () => {
    try {
      const params = {};
      if (filter === "mine_open") { params.assigned_to_me = true; params.status = "open"; }
      else if (filter === "mine_all") { params.assigned_to_me = true; }
      else if (filter === "all_open") { params.status = "open"; }
      const res = await api.get("/tasks", { params });
      setTasks(res.data);
    } catch (e) {
      toast.error("Ladefehler attività");
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const [u, c] = await Promise.all([
          api.get("/users/team").catch(() => ({ data: [] })),
          api.get("/clients").catch(() => ({ data: [] })),
        ]);
        setUsers(u.data);
        setClients(c.data);
      } catch (err) {
        console.warn("TasksPage: failed to load users/clients", err);
      }
    })();
  }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  useEffect(() => {
    setDraft((d) => ({ ...d, assigned_to: d.assigned_to || user?.id || "" }));
  }, [user]);

  const create = async () => {
    if (!draft.title.trim()) {
      toast.error("Eingeben un titolo");
      return;
    }
    try {
      const payload = { ...draft };
      if (!payload.due_date) delete payload.due_date;
      if (!payload.client_id) delete payload.client_id;
      await api.post("/tasks", payload);
      toast.success("Aufgabe erstellt");
      setOpen(false);
      setDraft({ title: "", description: "", due_date: "", priority: "medium", assigned_to: user?.id || "", client_id: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    }
  };

  const toggle = async (task) => {
    const next = task.status === "done" ? "open" : "done";
    try {
      await api.patch(`/tasks/${task.id}`, { status: next });
      load();
    } catch {
      toast.error("Fehler aggiornamento");
    }
  };

  const remove = async (task) => {
    if (!window.confirm(`Löschen l'attività "${task.title}"?`)) return;
    try {
      await api.delete(`/tasks/${task.id}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    }
  };

  const userById = (id) => users.find((u) => u.id === id);
  const clientById = (id) => clients.find((c) => c.id === id);

  const isOverdue = (t) => {
    if (!t.due_date || t.status === "done") return false;
    return new Date(t.due_date) < new Date(new Date().toDateString());
  };

  return (
    <div className="space-y-5" data-testid="tasks-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="label-uppercase text-prime">/ Aufgabe</div>
          <h1 className="font-display font-extrabold text-4xl lg:text-5xl tracking-tight mt-1">Meine Aufgaben</h1>
          <div className="text-sm text-muted-foreground mt-1">
            Verwalte Aufgaben und To-Dos — allgemein oder mit bestimmten Kunden verknüpft.
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-44 h-9" data-testid="tasks-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="mine_open">Meine offenen</SelectItem>
              <SelectItem value="mine_all">Meine (alle)</SelectItem>
              <SelectItem value="all_open">Alle offenen</SelectItem>
              <SelectItem value="all">Alle</SelectItem>
            </SelectContent>
          </Select>

          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="btn-prime gap-1" data-testid="new-task-btn">
                <Plus className="w-4 h-4" /> Neue Aufgabe
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Neue Aufgabe</DialogTitle>
                <DialogDescription>
                  Compito generale o collegato a un Kunde. Sarà visibile sulla Dashboard di chi è assegnatario.
                </DialogDescription>
              </DialogHeader>
              <TaskForm
                draft={draft} setDraft={setDraft}
                users={users} clients={clients}
              />
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)} data-testid="task-cancel">Abbrechen</Button>
                <Button className="btn-prime" onClick={create} data-testid="task-save">Anlegen</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Policy expiry auto-reminders */}
      <PolicyRemindersBanner />

      <div className="prime-card overflow-hidden">
        {tasks.length === 0 ? (
          <div className="p-12 text-center text-sm text-muted-foreground" data-testid="tasks-empty">
            Keine Aufgaben gefunden.
          </div>
        ) : (
          <div className="divide-y divide-border">
            {tasks.map((t) => {
              const prio = PRIORITY_META[t.priority] || PRIORITY_META.medium;
              const overdue = isOverdue(t);
              const assignee = userById(t.assigned_to);
              const client = t.client_id ? clientById(t.client_id) : null;
              return (
                <div key={t.id} className="flex items-start gap-3 p-4 hover:bg-secondary/50 transition-colors" data-testid={`task-row-${t.id}`}>
                  <Checkbox
                    checked={t.status === "done"}
                    onCheckedChange={() => toggle(t)}
                    className="mt-1"
                    data-testid={`task-toggle-${t.id}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${prio.color}`}>{prio.label}</span>
                      <h3 className={`font-medium ${t.status === "done" ? "line-through text-muted-foreground" : ""}`}>{t.title}</h3>
                      {overdue && (
                        <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-100 text-red-700 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" /> Scaduto
                        </span>
                      )}
                    </div>
                    {t.description && (
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{t.description}</p>
                    )}
                    <div className="flex items-center gap-3 text-xs text-muted-foreground mt-2 flex-wrap">
                      {assignee && (
                        <span className="flex items-center gap-1"><UserIcon className="w-3 h-3" /> {assignee.full_name}</span>
                      )}
                      {t.due_date && (
                        <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {fmtDate(t.due_date)}</span>
                      )}
                      {client && (
                        <button
                          onClick={() => navigate(`/clients/${client.id}`)}
                          className="flex items-center gap-1 text-prime hover:underline"
                          data-testid={`task-client-link-${t.id}`}
                        >
                          <ExternalLink className="w-3 h-3" /> {client.first_name} {client.last_name}
                        </button>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => remove(t)}
                    className="text-muted-foreground hover:text-prime p-1"
                    data-testid={`task-delete-${t.id}`}
                    title="Löschen"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export function TaskForm({ draft, setDraft, users, clients, lockedClientId }) {
  return (
    <div className="space-y-3">
      <div>
        <Label className="text-xs">Titel *</Label>
        <Input
          value={draft.title}
          onChange={(e) => setDraft({ ...draft, title: e.target.value })}
          placeholder="Es. Chiamare il Kunde per rinnovo Police auto"
          data-testid="task-title-input"
        />
      </div>
      <div>
        <Label className="text-xs">Beschreibung</Label>
        <Textarea
          value={draft.description}
          onChange={(e) => setDraft({ ...draft, description: e.target.value })}
          rows={3}
          data-testid="task-description-input"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Ablauf</Label>
          <Input
            type="date"
            value={draft.due_date}
            onChange={(e) => setDraft({ ...draft, due_date: e.target.value })}
            data-testid="task-due-input"
          />
        </div>
        <div>
          <Label className="text-xs">Priorität</Label>
          <Select value={draft.priority} onValueChange={(v) => setDraft({ ...draft, priority: v })}>
            <SelectTrigger className="h-9" data-testid="task-priority-input"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="low">Niedrig</SelectItem>
              <SelectItem value="medium">Mittel</SelectItem>
              <SelectItem value="high">Hoch</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Assegnato a</Label>
          <Select value={draft.assigned_to} onValueChange={(v) => setDraft({ ...draft, assigned_to: v })}>
            <SelectTrigger className="h-9" data-testid="task-assignee-input"><SelectValue placeholder="Auswählen" /></SelectTrigger>
            <SelectContent>
              {users.map((u) => (
                <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {!lockedClientId && (
          <div>
            <Label className="text-xs">Kunde (opzionale)</Label>
            <Select value={draft.client_id || "__none__"} onValueChange={(v) => setDraft({ ...draft, client_id: v === "__none__" ? "" : v })}>
              <SelectTrigger className="h-9" data-testid="task-client-input"><SelectValue placeholder="Keiner" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— Keine Kunden —</SelectItem>
                {clients.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        {lockedClientId && (
          <div>
            <Label className="text-xs">Kunde</Label>
            <div className="h-9 px-3 flex items-center border border-border rounded bg-secondary text-sm">
              {(clients.find((c) => c.id === lockedClientId) || {}).first_name} {(clients.find((c) => c.id === lockedClientId) || {}).last_name}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Compact dashboard widget
export function TasksWidget() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ open: 0, overdue: 0, due_today: 0 });
  const [tasks, setTasks] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [s, t] = await Promise.all([
          api.get("/tasks/stats/dashboard"),
          api.get("/tasks", { params: { assigned_to_me: true, status: "open" } }),
        ]);
        setStats(s.data);
        setTasks(t.data.slice(0, 5));
      } catch (err) {
        console.warn("TasksWidget: failed to load stats/tasks", err);
      }
    })();
  }, []);

  const toggle = async (task) => {
    try {
      await api.patch(`/tasks/${task.id}`, { status: "done" });
      setTasks((prev) => prev.filter((t) => t.id !== task.id));
      setStats((s) => ({ ...s, open: Math.max(0, s.open - 1) }));
    } catch (err) {
      console.warn("TasksWidget: failed to mark task done", err);
      toast.error("Impossibile completare l'attività");
    }
  };

  return (
    <div className="prime-card" data-testid="dashboard-tasks-widget">
      <div className="flex items-center justify-between px-6 py-4 border-b border-border">
        <div>
          <div className="label-uppercase text-muted-foreground">Aufgabe</div>
          <h2 className="font-display font-bold text-2xl tracking-tight flex items-center gap-2">
            <ClipboardCheck className="w-5 h-5 text-prime" /> Meine Aufgaben
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-right">
            <div className="text-xs text-muted-foreground">Offen / Scadute / Heute</div>
            <div className="font-mono text-sm">
              <span className="font-bold">{stats.open}</span>
              <span className="mx-1 text-muted-foreground">·</span>
              <span className={stats.overdue > 0 ? "text-prime font-bold" : ""}>{stats.overdue}</span>
              <span className="mx-1 text-muted-foreground">·</span>
              <span>{stats.due_today}</span>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate("/tasks")} data-testid="goto-tasks">
            Öffnen
          </Button>
        </div>
      </div>
      {tasks.length === 0 ? (
        <div className="p-8 text-center text-sm text-muted-foreground">
          Keine offene Aufgabe. Goditi un caffè ☕
        </div>
      ) : (
        <div className="divide-y divide-border">
          {tasks.map((t) => {
            const prio = PRIORITY_META[t.priority] || PRIORITY_META.medium;
            const overdue = t.due_date && t.status === "open" && new Date(t.due_date) < new Date(new Date().toDateString());
            return (
              <div key={t.id} className="flex items-center gap-3 px-6 py-3 hover:bg-secondary/40">
                <button
                  onClick={() => toggle(t)}
                  className="w-4 h-4 rounded border-2 border-border hover:border-prime transition-colors"
                  data-testid={`widget-task-done-${t.id}`}
                  aria-label="Als erledigt markieren"
                />
                <span className={`w-1.5 h-1.5 rounded-full ${prio.dot}`} />
                <span className="flex-1 text-sm truncate">{t.title}</span>
                {overdue && <Clock className="w-3 h-3 text-prime" />}
                {t.due_date && (
                  <span className={`text-xs font-mono ${overdue ? "text-prime" : "text-muted-foreground"}`}>
                    {fmtDate(t.due_date)}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
